import { render } from "preact";
import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";
import type { JSX } from "preact";
import { api, ApiError, type Operator } from "./api";
import { AgentSocket } from "./socket";
import { Login } from "./views/Login";
import { InboxView } from "./views/inbox/InboxView";
import { HistoryView } from "./views/history/HistoryView";
import { VisitorsView } from "./views/visitors/VisitorsView";
import { drafts } from "./views/inbox/Composer";
import { afterActivity, afterIdle, type IdleState } from "./lib/idle";
import { StatsView } from "./views/stats/StatsView";
import { SettingsView } from "./views/settings/SettingsView";
import { NavRail, type Presence } from "./components/NavRail";
import { EarlyAccessView } from "./views/platform/EarlyAccess";
import { CustomersView } from "./views/platform/Customers";
import { SetPasswordView } from "./views/SetPassword";
import { SuspendedScreen } from "./components/PlanNotice";
import { PlanContext, type WorkspacePlan } from "./lib/plan";
import { blockedBySuspension, effectiveRoute, useRoute } from "./lib/route";
import { applyTheme, nextTheme, readTheme, saveTheme, type ThemeChoice } from "./lib/theme";
import { DICTS, I18nContext, guessLang, rememberLang, type Lang } from "./lib/i18n";
import css from "./styles.css";
import { notify, readPrefs, registerServiceWorker } from "./lib/notify";
import { installDesktopLinks } from "./lib/desktop";
import { dueReminders } from "./lib/unanswered";
import { forgetPushBeforeSignOut } from "./lib/push";

/**
 * The console shell: who is signed in, which screen is showing, in which
 * language, and one live socket shared by everything that needs it.
 */

function App(): JSX.Element {
  const [operator, setOperator] = useState<Operator | null>(null);
  // Plan, limits and usage, from the same /agent/me: every screen reads it
  // from one context so the console never draws two different plans at once.
  const [workspace, setWorkspace] = useState<WorkspacePlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [route, go] = useRoute();
  const [theme, setTheme] = useState<ThemeChoice>(readTheme);
  const [presence, setPresence] = useState<Presence>("online");
  const [waiting, setWaiting] = useState(0);
  const [lang, setLang] = useState<Lang>(guessLang);

  // One socket for the whole session. Opening one per view would mean an
  // operator hears about a new conversation only on the screen they happen to
  // be looking at.
  const socket = useMemo(() => new AgentSocket(), []);
  const i18n = useMemo(() => ({ lang, t: DICTS[lang] }), [lang]);
  /** Whether "away" is the operator's own choice or the idle timer's; see lib/idle.ts. */
  const idle = useRef<IdleState>({ presence: "online", auto: false });
  /** True once the live connection has been down long enough to be worth saying. */
  const [dropped, setDropped] = useState(false);

  const applyPresence = useCallback(
    (next: IdleState) => {
      idle.current = next;
      setPresence(next.presence);
      socket.setPresence(next.presence);
    },
    [socket],
  );

  useEffect(() => applyTheme(theme), [theme]);
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  // The operator's saved language wins over the browser's once we know who it is.
  const adopt = useCallback(
    (next: Operator) => {
      // Before the operator is set — that is what opens the socket, and the
      // socket announces whatever status it holds the moment it connects. Set
      // afterwards, a reload would put someone who stepped away back in the
      // queue for as long as it took the profile to arrive.
      if (next.presence) applyPresence({ presence: next.presence, auto: false });
      setOperator(next);
      if (next.locale) {
        setLang(next.locale);
        rememberLang(next.locale);
      }
    },
    [applyPresence],
  );

  // No answer at all is not the same as "signed out". Showing the sign-in form
  // to an operator who merely lost the network sends them typing a password
  // that cannot work; the installed app opens offline often enough to matter.
  const [unreachable, setUnreachable] = useState(false);

  const checkSession = useCallback(() => {
    setLoading(true);
    api
      .me()
      .then((res) => {
        setUnreachable(false);
        setWorkspace(res.workspace ?? null);
        adopt(res.operator);
      })
      .catch((err) => {
        // 401 and 403 are simply "not signed in yet" here; anything else is
        // worth seeing in the console rather than swallowing.
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          setUnreachable(false);
          return;
        }
        setUnreachable(!(err instanceof ApiError));
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, [adopt]);

  useEffect(() => checkSession(), [checkSession]);

  // Plan only, and deliberately not through checkSession: that flips `loading`,
  // which swaps the whole shell for "Yükleniyor…" and remounts the screen —
  // taking with it the very banner ("payment received", "seats set to 6") the
  // refresh was meant to confirm.
  const refreshPlan = useCallback(() => {
    void api
      .me()
      .then((res) => setWorkspace(res.workspace ?? null))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!unreachable) return;
    window.addEventListener("online", checkSession);
    return () => window.removeEventListener("online", checkSession);
  }, [unreachable, checkSession]);

  useEffect(() => {
    if (!operator) return;
    socket.connect();
    return () => socket.close();
  }, [operator?.id, socket]);

  const changePresence = useCallback(
    (next: Presence) => applyPresence({ presence: next, auto: false }),
    [applyPresence],
  );

  // Stepping away without saying so: see lib/idle.ts.
  useEffect(() => {
    if (!operator) return;
    let lastInput = Date.now();
    const onInput = (): void => {
      lastInput = Date.now();
      const next = afterActivity(idle.current);
      if (next) applyPresence(next);
    };
    const events = ["pointerdown", "keydown", "mousemove", "wheel", "touchstart"] as const;
    for (const name of events) window.addEventListener(name, onInput, { passive: true });
    const timer = window.setInterval(() => {
      const next = afterIdle(idle.current, Date.now() - lastInput);
      if (next) applyPresence(next);
    }, 30_000);
    return () => {
      for (const name of events) window.removeEventListener(name, onInput);
      window.clearInterval(timer);
    };
  }, [operator?.id, applyPresence]);

  // A dropped connection is said out loud, but not a blip: a reconnect that
  // takes under a couple of seconds is not something to interrupt anyone for.
  useEffect(() => {
    let timer: number | null = null;
    const off = socket.on((event) => {
      if (event.t !== "socket.status") return;
      if (timer !== null) window.clearTimeout(timer);
      timer = null;
      if (event.data.connected) setDropped(false);
      else timer = window.setTimeout(() => setDropped(true), 2500);
    });
    return () => {
      off();
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [socket]);

  /**
   * A customer writing while the operator is on another screen.
   *
   * The inbox does its own listening, but it only exists while it is on
   * screen — so on Settings, Statistics or History a new chat used to arrive
   * in complete silence. Off the inbox, the shell rings and keeps the rail's
   * waiting count honest itself.
   */
  const routeRef = useRef(route);
  routeRef.current = route;
  const i18nRef = useRef(i18n);
  i18nRef.current = i18n;

  /**
   * "A customer has waited N minutes without an answer" (lib/unanswered.ts).
   *
   * Polled rather than pushed: a wait is the absence of an event, and nothing
   * on the socket says "still nobody answered". Every 20 seconds is plenty
   * for a threshold counted in minutes, and it runs on every screen — the
   * inbox shows the wait as a red strip, but only to someone looking at it.
   */
  const reminded = useRef(new Set<string>());
  useEffect(() => {
    if (!operator) return;
    const me = operator.id;
    const check = (): void => {
      const after = readPrefs().unansweredAfter;
      if (after <= 0) return;
      void api
        .conversations({ status: "active", assigned: "any", limit: 100 })
        .then((res) => {
          const t = i18nRef.current.t;
          for (const due of dueReminders(res.conversations, Date.now(), after, me, reminded.current)) {
            reminded.current.add(due.key);
            const row = res.conversations.find((r) => r.id === due.id)!;
            notify({
              title: t.notify.unansweredTitle,
              body: t.notify.unansweredBody(row.visitorName || row.visitorEmail || t.visitor(row.visitorId), due.minutes),
              tag: `unanswered-${due.id}`,
              hash: `#/inbox/${due.id}`,
              onClick: () => go({ view: "inbox", id: due.id }),
            });
          }
        })
        .catch(() => undefined);
    };
    check();
    const timer = window.setInterval(check, 20_000);
    return () => window.clearInterval(timer);
  }, [operator?.id, go]);
  useEffect(() => {
    if (!operator) return;
    let timer: number | null = null;
    const recount = (): void => {
      if (timer !== null) return;
      timer = window.setTimeout(() => {
        timer = null;
        void api
          .inboxCounts()
          .then((counts) => setWaiting(counts.pending))
          .catch(() => undefined);
      }, 400);
    };
    const open = (conversationId: number) => (): void => go({ view: "inbox", id: conversationId });
    const off = socket.on((event) => {
      if (routeRef.current.view === "inbox") return;
      const t = i18nRef.current.t;
      if (event.t === "conv.new") {
        const data = event.data as { conversationId: number; message?: { body?: string } };
        notify({
          title: t.notify.newChat,
          body: data.message?.body || t.notify.image,
          tag: `conv-${data.conversationId}`,
          hash: `#/inbox/${data.conversationId}`,
          onClick: open(data.conversationId),
        });
        recount();
      } else if (event.t === "msg.new") {
        const payload = event.data as { conversationId?: number; message?: { kind?: string; body?: string } };
        // Wrapped is how the site's room sends it; a bare one would be from a
        // conversation room, and no conversation is open on these screens.
        if (payload.conversationId && payload.message?.kind === "visitor") {
          notify({
            title: t.notify.newMessage(t.visitor(payload.conversationId)),
            body: payload.message.body || t.notify.image,
            tag: `conv-${payload.conversationId}`,
            hash: `#/inbox/${payload.conversationId}`,
            onClick: open(payload.conversationId),
          });
        }
        recount();
      } else if (event.t === "assigned") {
        const data = event.data;
        notify({
          title: data.by ? t.inbox.assignedBy(data.by.displayName) : t.inbox.assignedAuto,
          body: data.note || t.visitor(data.conversationId),
          tag: `assigned-${data.conversationId}`,
          hash: `#/inbox/${data.conversationId}`,
          onClick: open(data.conversationId),
        });
        recount();
      } else if (event.t === "mention") {
        const data = event.data;
        notify({
          title: t.inbox.mentionedBy(data.from.displayName),
          body: data.excerpt,
          tag: `mention-${data.messageId}`,
          hash: `#/inbox/${data.conversationId}`,
          onClick: open(data.conversationId),
        });
      } else if (event.t === "conv.update" || (event.t === "socket.status" && event.data.connected)) {
        recount();
      }
    });
    recount();
    return () => {
      off();
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [operator?.id, socket, go]);

  const changeLang = useCallback(async (next: Lang) => {
    setLang(next);
    rememberLang(next);
    await api.updateProfile({ locale: next });
    setOperator((current) => (current ? { ...current, locale: next } : current));
  }, []);

  // Views keep these in effect dependencies; a fresh arrow per render would
  // re-run those effects on every render of the shell.
  const selectInbox = useCallback((id: number | null) => go({ view: "inbox", id }), [go]);

  // This device stops receiving pushes *before* the session ends: telling the
  // server needs the session, and a subscription left behind keeps delivering
  // customers' messages to a console nobody is signed in to.
  const signOut = useCallback(() => {
    void forgetPushBeforeSignOut()
      .then(() => api.logout())
      .then(() => {
        socket.close();
        // Half-written replies belong to whoever wrote them, not to the next
        // person to sign in on this screen.
        drafts.clear();
        setDropped(false);
        setOperator(null);
      });
  }, [socket]);
  const selectHistory = useCallback((id: number | null) => go({ view: "history", id }), [go]);

  const isOwner = operator?.role === "owner";
  const isPlatformAdmin = operator?.isPlatformAdmin ?? false;
  const planState = useMemo(
    () => ({ plan: workspace, isOwner, reload: refreshPlan }),
    [workspace, isOwner, refreshPlan],
  );

  let screen: JSX.Element | null = null;
  // The invite link is opened by someone who has no password yet, so this
  // screen must render before — and instead of — the sign-in form.
  if (route.view === "setPassword") {
    screen = (
      <SetPasswordView
        token={route.token}
        onLang={(next) => {
          setLang(next);
          rememberLang(next);
        }}
      />
    );
  } else if (loading) {
    screen = <div class="empty">{i18n.t.common.loading}</div>;
  } else if (!operator && unreachable) {
    screen = (
      <div class="empty" role="alert">
        <p>{i18n.t.common.offline}</p>
        <button class="btn" onClick={checkSession}>
          {i18n.t.common.retry}
        </button>
      </div>
    );
  } else if (!operator) {
    screen = (
      <Login
        onLang={(next) => {
          setLang(next);
          rememberLang(next);
        }}
        onSignedIn={(signedIn) => {
          // The sign-in answer is the session, not the profile; the picture,
          // colour, language, plan and last-chosen status come from /agent/me.
          // Read first and adopted in one go, so the socket opens already
          // knowing whether this operator was away; the bare sign-in answer
          // is only the fallback if that read fails.
          void api
            .me()
            .then((res) => {
              setWorkspace(res.workspace ?? null);
              adopt(res.operator);
            })
            .catch(() => setOperator(signedIn));
        }}
      />
    );
  } else {
    // A direct link to the platform-admin screen, for anyone it isn't for,
    // renders the inbox instead — the same fallback SettingsView gives an
    // admin-only tab, so there is nothing at that URL to find by guessing it.
    const view = effectiveRoute(route, isPlatformAdmin);
    screen = workspace?.blocked && blockedBySuspension(view, { isOwner, isPlatformAdmin }) ? (
      <SuspendedScreen
        isOwner={isOwner}
        isPlatformAdmin={isPlatformAdmin}
        onSignOut={signOut}
      />
    ) : (
      <div class="shell">
        <NavRail
          route={view}
          go={go}
          waiting={waiting}
          operator={operator}
          presence={presence}
          onPresence={changePresence}
          theme={theme}
          onTheme={() => {
            const next = nextTheme(theme);
            saveTheme(next);
            setTheme(next);
          }}
          onSignOut={signOut}
        />
        <main class="view">
          {dropped ? (
            <div class="conn-banner" role="status">
              {i18n.t.common.connectionLost}
            </div>
          ) : null}
          {view.view === "inbox" ? (
            <InboxView
              operator={operator}
              socket={socket}
              selectedId={view.id}
              select={selectInbox}
              onWaiting={setWaiting}
            />
          ) : view.view === "visitors" ? (
            <VisitorsView onOpen={selectInbox} />
          ) : view.view === "history" ? (
            <HistoryView operator={operator} selectedId={view.id} select={selectHistory} />
          ) : view.view === "stats" ? (
            <StatsView />
          ) : view.view === "earlyAccess" ? (
            <EarlyAccessView />
          ) : view.view === "customers" ? (
            <CustomersView />
          ) : view.view === "settings" ? (
            <SettingsView
              operator={operator}
              tab={view.tab}
              onTab={(tab) => go({ view: "settings", tab })}
              onOperatorChange={setOperator}
              onLang={changeLang}
            />
          ) : null}
        </main>
      </div>
    );
  }

  return (
    <I18nContext.Provider value={i18n}>
      <PlanContext.Provider value={planState}>{screen}</PlanContext.Provider>
    </I18nContext.Provider>
  );
}

const style = document.createElement("style");
style.textContent = css;
document.head.appendChild(style);

render(<App />, document.getElementById("root")!);

// A tapped notification brings the console forward on the conversation it was about.
installDesktopLinks();
registerServiceWorker((hash) => {
  location.hash = hash;
});
